import { ApiErrorCode, isApiErrorCode } from '@seiri/shared/common'
import { ApiError } from '../api/utils/fetch-utils'
import { describeIssue } from './configure-zod'
import { ERRORS_SK } from './errors.sk'

const translate = (code: ApiErrorCode, params: unknown): string | undefined => {
  const translation = ERRORS_SK[code]
  if (typeof translation === 'string') return translation
  // A code that declares params always sends them; a body without them is not one we can word.
  // The cast is the price of indexing a mapped type with a union: `code` and `params` are
  // correlated by the catalogue, which TypeScript cannot follow through the lookup.
  return params === undefined ? undefined : (translation as (params: unknown) => string)(params)
}

/**
 * The sentence to show a user for a failed call. See docs/api-errors.md.
 *
 * - A code from `API_ERRORS` wins: it is specific ("the event is full") where the caller's text is
 *   generic ("could not register").
 * - A `validation.failed` lists its issues, worded as the form would word them.
 * - A response with no code - a plain exception, a proxy error page - shows `fallbackText`, the
 *   caller's description of what failed, or else the generic text for the status.
 * - A request with no response is an `ApiError` with code `network.failed` (status 0), so it takes
 *   the branch above. A bare `TypeError` - a `fetch` outside `fetchJson` - is read the same way.
 * - Anything else is not an `ApiError` and never reached the API: a bug.
 *
 * Never shows `error.message`: that is the backend's English, for developers.
 */
export const describeApiError = (error: unknown, fallbackText?: string): string => {
  if (!(error instanceof ApiError)) {
    return fallbackText ?? (error instanceof TypeError ? ERRORS_SK['network.failed'] : ERRORS_SK['http.serverError'])
  }
  const code = error.code
  if (!isApiErrorCode(code)) {
    return fallbackText ?? ERRORS_SK[code]
  }
  if (code === 'validation.failed') {
    const issues = [...new Set(error.issues.map(describeIssue))]
    return issues.length ? issues.join(' ') : ERRORS_SK['validation.failed']
  }
  return translate(code, error.params) ?? fallbackText ?? ERRORS_SK['http.badRequest']
}
